import apiClient from './api.service';
import type { RejectionPayload } from './rejection.service';

function unwrap<T>(response: { data?: any }): T {
  const d = response?.data?.data ?? response?.data;
  return d as T;
}

export const EST3_CONTRACT_SOURCE_PREFIX = 'contract:';

/** EST3 budget source: award id, or `contract:<id>` for a contract registered without an award. */
export function est3SourceParams(sourceId: string): { awardId?: string; contractId?: string } {
  return sourceId.startsWith(EST3_CONTRACT_SOURCE_PREFIX)
    ? { contractId: sourceId.slice(EST3_CONTRACT_SOURCE_PREFIX.length) }
    : { awardId: sourceId };
}

export interface Project {
  id: string;
  company_id: string;
  project_number: string;
  project_name: string;
  project_type?: string;
  customer_id?: string;
  sales_order_id?: string;
  customer_name: string;
  site_location?: string;
  project_status: string;
  project_manager?: string;
  department_id?: string | null;
  start_date?: string;
  planned_end_date?: string;
  end_date?: string;
  contract_value?: number;
  total_budgeted_cost?: number;
  budgeted_material_cost?: number;
  budgeted_labor_cost?: number;
  budgeted_subcontract_cost?: number;
  budgeted_equipment_cost?: number;
  budgeted_overhead_cost?: number;
  completion_percentage?: number;
  currency?: string;
  is_active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
  list_financials?: {
    ordered_total: number;
    estimation_budget: number;
    invoiced_total: number;
    posted_cost: number;
    forecast_profit: number;
    forecast_margin_pct: number;
    billed_profit: number;
    billed_margin_pct: number;
    any_bva_over?: boolean;
    eac?: number;
  } | null;
}

export interface PortfolioSummary {
  total_projects: number;
  active_projects: number;
  total_contract_value: number;
  hours_this_week: number;
  total_posted_cost: number;
  pending_timesheet_approvals: number;
}

export interface ProjectTimesheetRow {
  id: string;
  project_id: string;
  company_id: string;
  user_id: string;
  work_date: string;
  hours_worked: number;
  hourly_rate: number;
  total_cost: number;
  is_billable: boolean;
  billable_hours: number;
  billing_rate: number;
  billable_amount: number;
  activity_description?: string;
  task_category?: string;
  approval_status: string;
  approval_instance_id?: string | null;
  is_posted: boolean;
  posted_to_project_cost?: boolean;
  project_cost_id?: string | null;
  project?: { project_number?: string; project_name?: string };
  user?: { first_name?: string; last_name?: string; email?: string };
}

export interface ProjectCostRow {
  id: string;
  project_id: string;
  company_id: string;
  cost_date: string;
  cost_center_id: string;
  cost_category: string;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  currency: string;
  source_type?: string;
  description?: string;
  is_posted: boolean;
  project?: { project_number?: string; project_name?: string };
}

export type CreateProjectPayload = {
  project_name: string;
  customer_id?: string;
  customer_name: string;
  site_location?: string;
  project_type?: string;
  project_status?: string;
  project_manager?: string;
  department_id?: string | null;
  start_date?: string;
  planned_end_date?: string;
  contract_value?: number;
  currency?: string;
  description?: string;
  budgeted_material_cost?: number;
  budgeted_labor_cost?: number;
  budgeted_subcontract_cost?: number;
  budgeted_equipment_cost?: number;
  budgeted_overhead_cost?: number;
  is_active?: boolean;
};

class ProjectService {
  async getProjects(filters?: {
    project_status?: string;
    project_manager?: string;
    customer_id?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<Project[]> {
    try {
      const response = await apiClient.get('/projects', { params: filters });
      const projects = response.data?.data || response.data || [];
      return Array.isArray(projects) ? projects : [];
    } catch (error: any) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  }

  async getProjectById(id: string): Promise<Project> {
    const response = await apiClient.get(`/projects/${id}`);
    return unwrap<Project>(response);
  }

  async createProject(data: CreateProjectPayload): Promise<Project> {
    const response = await apiClient.post('/projects', data);
    return unwrap<Project>(response);
  }

  async updateProject(id: string, data: Partial<CreateProjectPayload>): Promise<Project> {
    const response = await apiClient.put(`/projects/${id}`, data);
    return unwrap<Project>(response);
  }

  async deleteProject(id: string): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  }

  async getProjectsForSelection(): Promise<Project[]> {
    try {
      const body = await apiClient.get('/projects/selection/list');
      // api.service.get returns { success, data } already (not AxiosResponse)
      const projects = (body as any)?.data ?? body;
      if (Array.isArray(projects)) return projects;
      return [];
    } catch (error) {
      console.error('Error fetching projects for selection:', error);
      // Fall back to full list endpoint (always registered at GET /projects)
      try {
        return await this.getProjects({ is_active: true });
      } catch {
        return [];
      }
    }
  }

  async quickCreateProject(data: {
    project_name: string;
    customer_name: string;
    customer_id?: string;
    start_date: string;
    department_id?: string;
  }): Promise<Project> {
    const response = await apiClient.post('/projects/quick-create', data);
    return response.data?.data || response.data;
  }

  async getPortfolioSummary(): Promise<PortfolioSummary> {
    const response = await apiClient.get('/projects/portfolio-summary');
    return unwrap<PortfolioSummary>(response);
  }

  async getCompanyTimesheets(params?: {
    project_id?: string;
    user_id?: string;
    approval_status?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ProjectTimesheetRow[]> {
    const response = await apiClient.get('/projects/timesheets', { params });
    const rows = response.data?.data || response.data || [];
    return Array.isArray(rows) ? rows : [];
  }

  async getCompanyCosts(params?: {
    project_id?: string;
    cost_category?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ProjectCostRow[]> {
    const response = await apiClient.get('/projects/costs', { params });
    const rows = response.data?.data || response.data || [];
    return Array.isArray(rows) ? rows : [];
  }

  async getProjectCostCenters(): Promise<
    { id: string; cost_center_code: string; cost_center_name: string; cost_category: string }[]
  > {
    const response = await apiClient.get('/projects/cost-centers');
    const rows = response.data?.data || response.data || [];
    return Array.isArray(rows) ? rows : [];
  }

  async submitTimesheetForUniversalApproval(timesheetId: string): Promise<ProjectTimesheetRow> {
    const response = await apiClient.post(`/projects/timesheets/${timesheetId}/submit-for-universal-approval`, {});
    return unwrap<ProjectTimesheetRow>(response);
  }

  async approveTimesheet(timesheetId: string, remarks?: string): Promise<ProjectTimesheetRow> {
    const response = await apiClient.post(`/projects/timesheets/${timesheetId}/approve`, {
      remarks: remarks ?? undefined,
    });
    return unwrap<ProjectTimesheetRow>(response);
  }

  async rejectTimesheet(timesheetId: string, payload: RejectionPayload): Promise<ProjectTimesheetRow> {
    const response = await apiClient.post(`/projects/timesheets/${timesheetId}/reject`, payload);
    return unwrap<ProjectTimesheetRow>(response);
  }

  async postTimesheetToCost(
    timesheetId: string,
    body: {
      costCenterId: string;
      wbsNodeId?: string | null;
      cbsCodeId?: string | null;
      costCategory?: string;
    }
  ): Promise<ProjectTimesheetRow> {
    const response = await apiClient.post(`/projects/timesheets/${timesheetId}/post-to-cost`, body);
    return unwrap<ProjectTimesheetRow>(response);
  }

  async createTimesheet(
    projectId: string,
    body: {
      work_date: string;
      hours_worked: number;
      hourly_rate?: number;
      is_billable?: boolean;
      billable_hours?: number;
      billing_rate?: number;
      activity_description?: string;
      task_category?: string;
      user_id?: string;
      department_id?: string;
    }
  ): Promise<ProjectTimesheetRow> {
    const response = await apiClient.post(`/projects/${projectId}/timesheets`, body);
    return unwrap<ProjectTimesheetRow>(response);
  }

  async createProjectCost(
    projectId: string,
    body: {
      cost_date: string;
      cost_center_id: string;
      cost_category: string;
      quantity?: number;
      unit_cost?: number;
      total_cost: number;
      currency?: string;
      source_type?: string;
      description?: string;
      wbs_node_id?: string | null;
      cbs_code_id?: string | null;
    }
  ): Promise<ProjectCostRow> {
    const response = await apiClient.post(`/projects/${projectId}/costs`, body);
    return unwrap<ProjectCostRow>(response);
  }

  async postProjectCost(projectId: string, costId: string): Promise<ProjectCostRow> {
    const response = await apiClient.post(`/projects/${projectId}/costs/${costId}/post`);
    return unwrap<ProjectCostRow>(response);
  }

  /** `sourceId` is an award id, or `contract:<id>` for a contract registered without an award. */
  async getEst3BudgetPreview(projectId: string, sourceId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/est3-preview`, {
      params: est3SourceParams(sourceId),
    });
    return unwrap<Record<string, unknown>>(response);
  }

  async convertBudgetFromEst3(
    projectId: string,
    payload: { awardId?: string; contractId?: string; previewHash?: string }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.post(`/projects/${projectId}/budgets/from-est3`, payload);
    return unwrap<Record<string, unknown>>(response);
  }

  async getEst3BudgetLineage(projectId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/est3-lineage`);
    return unwrap<Record<string, unknown>>(response);
  }

  async listEst3AwardsForBudget(projectId: string): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/est3-awards`);
    return unwrap<Record<string, unknown>[]>(response);
  }

  async listEst3ContractsForBudget(projectId: string): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/est3-contracts`);
    return unwrap<Record<string, unknown>[]>(response);
  }

  async listBudgetRevisions(projectId: string): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/revisions`);
    return unwrap<Record<string, unknown>[]>(response);
  }

  async getBudgetBaseline(projectId: string): Promise<{
    revision: Record<string, unknown> | null;
    lines: Record<string, unknown>[];
  }> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/baseline`);
    return unwrap(response);
  }

  async getBudgetStructureContext(projectId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/structure-context`);
    return unwrap(response);
  }

  async seedBudgetFromWbs(
    projectId: string,
    payload?: {
      revisionId?: string;
      leafOnly?: boolean;
      categories?: string[];
      replaceLines?: boolean;
    }
  ): Promise<{ revision: Record<string, unknown>; lines: Record<string, unknown>[] }> {
    const response = await apiClient.post(`/projects/${projectId}/budgets/seed-from-wbs`, payload || {});
    return unwrap(response);
  }

  async getBudgetRevision(
    projectId: string,
    revId: string
  ): Promise<{ revision: Record<string, unknown>; lines: Record<string, unknown>[] }> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/revisions/${revId}`);
    return unwrap(response);
  }

  async getBudgetRevisionById(
    revisionId: string
  ): Promise<{ revision: Record<string, unknown>; lines: Record<string, unknown>[] }> {
    const response = await apiClient.get(`/projects/budget-revisions/${revisionId}`);
    return unwrap(response);
  }

  async createBudgetRevision(
    projectId: string,
    payload: {
      mode: 'blank' | 'copy';
      parentRevisionId?: string;
      sourceType?: string;
      changeReason?: string;
      notes?: string;
      department_id?: string;
    }
  ): Promise<{ revision: Record<string, unknown>; lines: Record<string, unknown>[] }> {
    const response = await apiClient.post(`/projects/${projectId}/budgets/revisions`, payload);
    return unwrap(response);
  }

  async replaceBudgetRevisionLines(
    projectId: string,
    revId: string,
    lines: Record<string, unknown>[]
  ): Promise<{ revision: Record<string, unknown>; lines: Record<string, unknown>[] }> {
    const response = await apiClient.patch(`/projects/${projectId}/budgets/revisions/${revId}/lines`, {
      lines,
      replaceAll: true,
    });
    return unwrap(response);
  }

  async submitBudgetRevision(projectId: string, revId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.post(`/projects/${projectId}/budgets/revisions/${revId}/submit`);
    return unwrap(response);
  }

  async getCostControlAvailability(
    projectId: string,
    params: { wbsNodeId?: string; cbsCodeId?: string; costCategory?: string }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/cost-control/availability`, {
      params: {
        wbsNodeId: params.wbsNodeId,
        cbsCodeId: params.cbsCodeId,
        costCategory: params.costCategory,
      },
    });
    return unwrap(response);
  }

  async checkCostControl(
    projectId: string,
    payload: {
      lines: Array<Record<string, unknown>>;
      excludeSourceType?: string;
      excludeSourceId?: string;
      sourceEntityType?: string;
      sourceEntityId?: string;
    }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.post(`/projects/${projectId}/cost-control/check`, payload);
    return unwrap(response);
  }

  async getCostControlSummary(projectId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/cost-control/summary`);
    return unwrap(response);
  }

  async getBudgetVsActual(
    projectId: string,
    groupBy: 'wbs' | 'cbs' | 'category' | 'line' = 'line'
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/cost-control/budget-vs-actual`, {
      params: { groupBy },
    });
    return unwrap(response);
  }

  async listCostControlEvents(
    projectId: string,
    params?: {
      wbsNodeId?: string | null;
      costCategory?: string | null;
      cbsCodeId?: string | null;
      limit?: number;
    }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/cost-control/events`, {
      params: {
        wbsNodeId: params?.wbsNodeId || undefined,
        costCategory: params?.costCategory || undefined,
        cbsCodeId: params?.cbsCodeId || undefined,
        limit: params?.limit,
      },
    });
    return unwrap(response);
  }

  async listBudgetExceedances(projectId: string): Promise<Record<string, unknown>[]> {
    const response = await apiClient.get(`/projects/${projectId}/budgets/exceedances`);
    return unwrap(response);
  }

  async createBudgetExceedance(
    projectId: string,
    payload: {
      sourceEntityType: string;
      sourceEntityId: string;
      reason?: string;
      department_id?: string;
      lines: Array<Record<string, unknown>>;
    }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.post(`/projects/${projectId}/budgets/exceedances`, payload);
    return unwrap(response);
  }

  async submitBudgetExceedance(
    projectId: string,
    exceedanceId: string
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.post(
      `/projects/${projectId}/budgets/exceedances/${exceedanceId}/submit`
    );
    return unwrap(response);
  }

  async getBudgetExceedanceById(exceedanceId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/budget-exceedances/${exceedanceId}`);
    return unwrap(response);
  }

  async getFinancialPosition(projectId: string): Promise<Record<string, unknown>> {
    const response = await apiClient.get(`/projects/${projectId}/financial-position`);
    return unwrap(response);
  }

  async runOverheadAllocation(
    projectId: string,
    payload: {
      period: string;
      amount: number;
      costCenterId: string;
      notes?: string;
    }
  ): Promise<Record<string, unknown>> {
    const response = await apiClient.post(`/projects/${projectId}/overhead-allocation/run`, {
      period: payload.period,
      amount: payload.amount,
      cost_center_id: payload.costCenterId,
      notes: payload.notes,
    });
    return unwrap(response);
  }

  async downloadBudgetImportTemplate(projectId: string): Promise<Blob> {
    // apiClient.get() already returns axios response.data; use raw client for blobs.
    const res = await apiClient.getClient().get(`/projects/${projectId}/budgets/import/template`, {
      responseType: 'blob',
    });
    const data = res.data;
    if (data instanceof Blob) return data;
    return new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async exportBudgetRevision(projectId: string, revId: string): Promise<Blob> {
    const res = await apiClient.getClient().get(
      `/projects/${projectId}/budgets/revisions/${revId}/export`,
      { responseType: 'blob' }
    );
    const data = res.data;
    if (data instanceof Blob) return data;
    return new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async previewBudgetImport(projectId: string, file: File): Promise<{
    preview_token?: string;
    sheet?: string;
    row_count: number;
    valid_count: number;
    skipped_count: number;
    error_count: number;
    mapped_total: number;
    ready: boolean;
    rows: Array<Record<string, unknown>>;
    error_summary?: Array<{
      issue: string;
      count: number;
      excel_rows: number[];
      values: string[];
      how_to_fix: string;
    }>;
    reference?: Record<string, unknown>;
    next_steps?: string;
    wbs_to_create?: Array<Record<string, unknown>>;
    wbs_create_count?: number;
  }> {
    const fd = new FormData();
    fd.append('file', file);
    // Do not set Content-Type manually — axios must add multipart boundary.
    const response = await apiClient.post(`/projects/${projectId}/budgets/import/preview`, fd);
    return unwrap(response);
  }

  async commitBudgetImport(
    projectId: string,
    file: File,
    opts?: { revisionId?: string; changeReason?: string }
  ): Promise<{
    revision: Record<string, unknown>;
    lines: Record<string, unknown>[];
    wbs_created?: Array<Record<string, unknown>>;
    wbs_created_count?: number;
  }> {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.revisionId) fd.append('revisionId', opts.revisionId);
    if (opts?.changeReason) fd.append('changeReason', opts.changeReason);
    const response = await apiClient.post(`/projects/${projectId}/budgets/import/commit`, fd);
    return unwrap(response);
  }
}

export const projectService = new ProjectService();
